import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from '@cimpress/react-components/lib/Modal';
import find from 'lodash.find';
import SelectorModal from './SelectorModal';
import { getFulfillers } from '../../services/fulfillerIdentity';
import { getMerchants } from '../../services/merchantConfig';
import { getSavedTenants, putSavedTenants } from '../../services/customizr';
import { MERCHANT, FULFILLER } from '../../constants';

const noop = () => {};

/* eslint-disable jsx-a11y/no-static-element-interactions */
const HeaderLink = ({ selectedTenant, onClick }) => (
  <span>
    {selectedTenant && 'Acting as: '}
    <a className="clickable" onClick={onClick}>
      {selectedTenant || 'Select profile'}
    </a>
  </span>
);
/* eslint-enable jsx-a11y/no-static-element-interactions */

HeaderLink.propTypes = {
  selectedTenant: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

HeaderLink.defaultProps = {
  selectedTenant: undefined,
  onClick: undefined,
};

export class TenantSelector extends Component {
  static propTypes = {
    authToken: PropTypes.string.isRequired,
    onCurrentTenantChange: PropTypes.func.isRequired,
    resourceType: PropTypes.string,
    initialTenantType: PropTypes.oneOf([MERCHANT, FULFILLER]),
    initialTenantId: PropTypes.string,
    forceSelection: PropTypes.bool,
    show: PropTypes.bool,
    environment: PropTypes.string,
    showLink: PropTypes.bool,
    onClose: PropTypes.func,
    modalTitle: PropTypes.string,
    modalHelpText: PropTypes.string,
    onReady: PropTypes.func,
    onInitialTenantAccessDenied: PropTypes.func,
  };

  static defaultProps = {
    environment: 'prd',
    resourceType: '',
    forceSelection: false,
    showLink: false,
    show: false,
    onClose: noop,
    modalTitle: 'Choose who to view',
    modalHelpText: '',
    onReady: noop,
    onInitialTenantAccessDenied: noop,
  };

  static allowedFulfillers;
  static allowedMerchants;
  static savedTenants;

  constructor(props) {
    super(props);
    this.state = {
      selectedTenant: null, // backwards-compatibility with Customizr
      selectedMerchant: null,
      selectedFulfiller: null,
      allowedMerchants: [],
      allowedFulfillers: [],
      showModal: false,
    };
  }

  componentDidMount() {
    this.loadTenants();
    if (this.props.resourceType) {
      this.setTenantTypeStateFromProps();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.resourceType !== prevProps.resourceType) {
      this.setTenantTypeStateFromProps();
    }

    if (this.props.show && !prevProps.show) {
      this.showModal();
    } else if (!this.props.show && prevProps.show && this.state.showModal) {
      this.setState({ showModal: false });
    }
  }

  setTenantTypeStateFromProps() {
    this.setState({ tenantType: this.props.resourceType });
  }

  loadTenants = () => {
    this.setState({ fetchingFulfillers: true, fetchingMerchants: true, fetchingSettings: true });
    const { resourceType } = this.props;
    // load data if it doesn't exist on the static members
    Promise.all([
      !TenantSelector.allowedFulfillers
        ? getFulfillers(this.props.authToken, this.props.environment)
        : Promise.resolve(TenantSelector.allowedFulfillers),
      !TenantSelector.allowedMerchants
        ? getMerchants(this.props.authToken, this.props.environment)
        : Promise.resolve(TenantSelector.allowedMerchants),
      !TenantSelector.savedTenants
        ? getSavedTenants(this.props.authToken, this.props.environment)
        : Promise.resolve(TenantSelector.savedTenants),
    ])
      .then(([fulfillers, merchants, savedTenants]) => {
        const { initialTenantType, initialTenantId, onInitialTenantAccessDenied } = this.props;
        TenantSelector.allowedMerchants = merchants || TenantSelector.allowedMerchants;
        TenantSelector.allowedFulfillers = fulfillers || TenantSelector.allowedFulfillers;
        TenantSelector.savedTenants = savedTenants || {};

        const allowedMerchants = TenantSelector.allowedMerchants || [];
        const allowedFulfillers = TenantSelector.allowedFulfillers || [];
        let { selectedTenant } = this.state;
        let restoredTenant;
        let initialTenantSuccess = false;

        if (initialTenantId && initialTenantType) {
          let tenant;
          switch (initialTenantType) {
            case MERCHANT: {
              tenant = allowedMerchants.find(m => m.merchantId === initialTenantId);
              if (tenant) {
                restoredTenant = { tenant, type: MERCHANT };
              }
              break;
            }
            case FULFILLER: {
              tenant = allowedFulfillers.find(
                f => f.fulfillerId === initialTenantId || String(f.fulfillerId) === initialTenantId
              );
              if (tenant) {
                restoredTenant = { tenant, type: FULFILLER };
              }
              break;
            }
          }

          if (restoredTenant) {
            initialTenantSuccess = true;
          } else {
            onInitialTenantAccessDenied({ initialTenantId, initialTenantType });
          }
        }

        if (
          !initialTenantSuccess &&
          savedTenants.selectedTenant &&
          (!resourceType || savedTenants.selectedTenant.tenantType === resourceType)
        ) {
          switch (savedTenants.selectedTenant.resourceType) {
            case MERCHANT:
              restoredTenant = {
                tenant: allowedMerchants.find(m => m.merchantId === savedTenants.selectedTenant.value),
                type: MERCHANT,
              };
              break;

            case FULFILLER:
              restoredTenant = {
                // checking both id types to more gracefully migrate to the newer fulfillerId value
                tenant: allowedFulfillers.find(
                  f =>
                    f.fulfillerId === savedTenants.selectedTenant.value ||
                    f.internalFulfillerId === savedTenants.selectedTenant.value
                ),
                type: FULFILLER,
              };
              break;
          }
        } else if (resourceType === MERCHANT && savedTenants.recentMerchant) {
          restoredTenant = {
            tenant: allowedMerchants.find(m => m.merchantId === savedTenants.recentMerchant.value),
            type: MERCHANT,
          };
        } else if (resourceType === FULFILLER && savedTenants.recentFulfiller) {
          restoredTenant = {
            // checking both id types to more gracefully migrate to the newer fulfillerId value
            tenant: allowedFulfillers.find(
              f =>
                f.fulfillerId === savedTenants.recentFulfiller.value ||
                f.internalFulfillerId === savedTenants.recentFulfiller.value
            ),
            type: FULFILLER,
          };
        }

        if (restoredTenant && restoredTenant.tenant) {
          let details = this.onChange(restoredTenant, initialTenantSuccess ? this.save : undefined);
          selectedTenant = {
            value: details.tenantId,
            label: details.tenantName,
            resourceType: details.tenantType,
          };
        }

        let selectedMerchant = savedTenants.recentMerchant && savedTenants.recentMerchant.value;
        let selectedFulfiller = savedTenants.recentFulfiller && savedTenants.recentFulfiller.value;

        // if there is a selected tenant notify the application
        if (selectedTenant) {
          if (selectedTenant.resourceType === MERCHANT && selectedTenant.value) {
            selectedMerchant = selectedTenant.value;
          }
          if (selectedTenant.resourceType === FULFILLER && selectedTenant.value) {
            selectedFulfiller = selectedTenant.value;
          }
          this.props.onCurrentTenantChange(selectedTenant);
        }

        //notify the application that the selector is ready
        this.props.onReady();

        this.setState({
          fetchingFulfillers: false,
          fetchingMerchants: false,
          fetchingSettings: false,
          allowedMerchants,
          allowedFulfillers,
          selectedTenant,
          selectedMerchant,
          selectedFulfiller,
        });
      })
      .catch(error => console.error(error));
  };

  onChange = (tenant, cb) => {
    const selection = tenant.tenant || {};
    const newState = {
      tenantType: tenant.type,
    };

    if (tenant.type === MERCHANT) {
      newState.tenantId = selection.merchantId;
      newState.tenantName = selection.displayName;
      newState.selectedMerchant = selection.merchantId;
    } else {
      newState.tenantId = selection.fulfillerId;
      newState.tenantName = selection.name;
      newState.selectedFulfiller = selection.fulfillerId;
    }

    this.setState(newState, cb);
    return newState;
  };

  showModal = () => {
    const { value, label, resourceType } = this.state.selectedTenant || {};
    this.setState({
      showModal: true,
      tenantId: value,
      tenantName: label,
      tenantType: resourceType || this.props.resourceType,
    });
  };

  closeModal = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
    this.setState({
      showModal: false,
    });
  };

  save = () => {
    const { tenantId, tenantName, tenantType } = this.state;
    const tenant = {
      value: tenantId,
      label: tenantName,
      resourceType: tenantType,
    };
    this.setState({ selectedTenant: tenant });
    const savedSetting = { selectedTenant: tenant };
    if (tenantType === MERCHANT) {
      savedSetting.recentMerchant = tenant;
    }
    if (tenantType === FULFILLER) {
      savedSetting.recentFulfiller = tenant;
      const fulfiller = find(this.state.allowedFulfillers, tenant => tenant.fulfillerId === tenantId);
      tenant.altValue = fulfiller ? fulfiller.internalFulfillerId : null;
    }
    TenantSelector.savedTenants = { ...TenantSelector.savedTenants, ...savedSetting };
    putSavedTenants(this.props.authToken, this.props.environment, TenantSelector.savedTenants);

    this.props.onCurrentTenantChange(tenant);
    this.closeModal();
  };

  render() {
    const {
      selectedTenant,
      selectedMerchant,
      selectedFulfiller,
      showModal,
      tenantType,
      tenantName,
      tenantId,
      allowedMerchants,
      allowedFulfillers,
      fetchingFulfillers,
      fetchingMerchants,
    } = this.state;
    const { label } = selectedTenant || {};
    const { authToken, resourceType, forceSelection, showLink, show, modalTitle, modalHelpText } = this.props;
    const mustSelectTenant = !selectedTenant && forceSelection;

    if (!fetchingFulfillers && !fetchingMerchants && allowedMerchants.length + allowedFulfillers.length === 0) {
      return (
        <Modal
          className="modal-overflow"
          show={showModal || show || mustSelectTenant}
          title={'No permissions'}
          onRequestHide={this.closeModal}
          closeButton={!mustSelectTenant}
          bsStyle={'danger'}
          footer={
            <div className="pull-right">
              <button className="btn btn-default" disabled={mustSelectTenant} onClick={this.closeModal}>
                Close
              </button>
            </div>
          }>
          You do not have permission to access this application. Please contact your administrator to provide you with
          access. If you don’t know who your administrator is, contact{' '}
          <a
            href="https://cimpress-support.atlassian.net/servicedesk/customer/portal/6"
            target="_blank"
            rel="noopener noreferrer">
            Platform Support.
          </a>
        </Modal>
      );
    }

    // TODO: fix test environment so that document is not undefined
    // @cimpress/react-components/lib/Modal blows up on any reference to document.body
    return (
      <div style={showLink ? { marginTop: '13px' } : {}}>
        {showLink ? <HeaderLink onClick={this.showModal} selectedTenant={label} /> : null}
        <SelectorModal
          authToken={authToken}
          show={showModal || show}
          selectedMerchant={selectedMerchant}
          selectedFulfiller={selectedFulfiller}
          mustSelectTenant={mustSelectTenant}
          onClose={this.closeModal}
          onSave={this.save}
          canSave={!!(tenantId && tenantName && tenantType)}
          loading={fetchingFulfillers || fetchingMerchants}
          onChange={this.onChange}
          merchants={allowedMerchants}
          fulfillers={allowedFulfillers}
          resourceType={resourceType || tenantType || ''}
          title={modalTitle}
          helpText={modalHelpText}
        />
      </div>
    );
  }
}

export default TenantSelector;
